import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { api } from "../api/axios";
import { EmptyState, PageHeader, Pill, SectionCard, StatTile } from "../components/ui";

const STATUS_TONE = { pass: "success", fail: "error", na: "neutral", unknown: "warning" };

function statusLabel(s) {
  if (s === "pass") return "עובר";
  if (s === "fail") return "נכשל";
  if (s === "na") return "לא רלוונטי";
  return "לא ידוע";
}

export default function ComplianceCenter() {
  const { id } = useParams();
  const theme = useTheme();
  const BackIcon = theme.direction === "rtl" ? ArrowForwardRoundedIcon : ArrowBackRoundedIcon;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState(null);
  const [snapshotBusy, setSnapshotBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/api/compliance/sites/${id}`);
    setData(data);
  }, [id]);

  const loadHistory = useCallback(async () => {
    const { data } = await api.get(`/api/compliance/sites/${id}/history?limit=10`);
    setHistory(data);
  }, [id]);

  useEffect(() => {
    load().catch((err) => setError(err?.response?.data?.message || "שגיאה"));
    loadHistory().catch(() => null);
  }, [load, loadHistory]);

  const update = async (checkId, status, notes) => {
    await api.put(`/api/compliance/sites/${id}/${checkId}`, { status, notes });
    await load();
  };

  const createSnapshot = async () => {
    setSnapshotBusy(true);
    try {
      await api.post(`/api/compliance/sites/${id}/snapshot`);
      await loadHistory();
    } finally {
      setSnapshotBusy(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data)
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={60} width="50%" />
        <Skeleton variant="rounded" height={140} />
        <Skeleton variant="rounded" height={320} />
      </Stack>
    );

  const rawPct = data.score && typeof data.score === "object" ? data.score.percent : data.score;
  const percent = rawPct != null && rawPct !== "" ? Math.max(0, Math.min(100, Number(rawPct || 0))) : null;
  const scoreTone = percent == null ? "neutral" : percent >= 80 ? "success" : percent >= 60 ? "warning" : "error";

  return (
    <Box>
      <Button component={RouterLink} to={`/sites/${id}`} size="small" startIcon={<BackIcon />} sx={{ mb: 1.5, color: "text.secondary", minHeight: 30, px: 1 }}>
        {data.site.name}
      </Button>
      <PageHeader
        title="מרכז תאימות"
        subtitle={
          <>
            אתר: <b>{data.site.name}</b> · {data.site.domain}
          </>
        }
        actions={
          <Button component={RouterLink} to={`/sites/${id}/compliance-report`} variant="contained" startIcon={<PrintOutlinedIcon />}>
            דוח / הדפסה
          </Button>
        }
      />

      {/* Score + summary */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1.3fr 1fr 1fr 1fr 1fr" }, gap: 2, mb: 3 }}>
        <Card sx={{ p: 2.5, gridColumn: { xs: "1 / -1", md: "auto" }, display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ position: "relative", width: 72, height: 72, flex: "none" }}>
            <CircularProgress variant="determinate" value={100} size={72} thickness={5} sx={{ color: (t) => t.aw.soft, position: "absolute" }} />
            <CircularProgress
              variant="determinate"
              value={percent || 0}
              size={72}
              thickness={5}
              color={scoreTone === "neutral" ? "inherit" : scoreTone}
              aria-label={percent == null ? "אין ציון" : `ציון ${percent}%`}
              sx={{ "& circle": { strokeLinecap: "round" } }}
            />
            <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontWeight: 800, fontSize: "1.05rem" }}>
              {percent == null ? "—" : `${percent}%`}
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" component="h2">
              ציון תאימות
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: "1.4rem" }}>{data.score?.grade ? `דירוג ${data.score.grade}` : percent == null ? "לא חושב" : "—"}</Typography>
          </Box>
        </Card>
        {[
          { label: "עובר", value: data.summary.pass, tone: "success", icon: <CheckCircleOutlineRoundedIcon /> },
          { label: "נכשל", value: data.summary.fail, tone: "error", icon: <HighlightOffRoundedIcon /> },
          { label: "לא ידוע", value: data.summary.unknown, tone: "warning", icon: <HelpOutlineRoundedIcon /> },
          { label: "High fails", value: data.summary.fail_high || 0, tone: data.summary.fail_high ? "error" : "neutral", icon: <PriorityHighRoundedIcon /> },
        ].map((s) => (
          <StatTile key={s.label} label={s.label} value={s.value ?? 0} tone={s.tone} icon={s.icon} />
        ))}
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <SectionCard
            title="צ׳קליסט תאימות"
            description="עדכנו סטטוס והערות לכל בדיקה — נשמר אוטומטית"
            icon={<ChecklistRoundedIcon />}
            contentSx={{ p: 0 }}
          >
            <Alert severity="info" sx={{ mx: 3, mb: 2 }}>
              זה צ’קליסט תפעולי (MVP) שמטרתו לעזור ליישם ולעקוב. בשלב הבא נחבר AI Audit שימלא חלק מהבדיקות אוטומטית (ALT, קונטרסט,
              labels וכו׳) ויפיק דוח מסודר.
            </Alert>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
              {data.items.map((it) => (
                <Box component="li" key={it.check_id} sx={{ px: 3, py: 2.25, borderTop: 1, borderColor: "divider", display: "grid", gap: 1.25 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start" useFlexGap flexWrap="wrap">
                    <Typography component="h3" sx={{ fontWeight: 600, fontSize: "0.95rem", flexGrow: 1, minWidth: 200 }}>
                      {it.title}
                    </Typography>
                    <Pill tone={STATUS_TONE[it.status] || "warning"}>{statusLabel(it.status)}</Pill>
                    <Pill tone={it.severity === "high" ? "error" : "neutral"}>{it.severity}</Pill>
                    {it.standard_ref ? <Pill tone="neutral">{it.standard_ref}</Pill> : null}
                  </Stack>
                  {it.description ? (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {it.description}
                    </Typography>
                  ) : null}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      select
                      size="small"
                      label="סטטוס"
                      value={it.status}
                      onChange={(e) => update(it.check_id, e.target.value, it.notes)}
                      sx={{ minWidth: 180 }}
                    >
                      <MenuItem value="unknown">לא ידוע</MenuItem>
                      <MenuItem value="pass">עובר</MenuItem>
                      <MenuItem value="fail">נכשל</MenuItem>
                      <MenuItem value="na">לא רלוונטי</MenuItem>
                    </TextField>
                    <TextField
                      size="small"
                      label="הערות"
                      value={it.notes || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setData((prev) => ({
                          ...prev,
                          items: prev.items.map((x) => (x.check_id === it.check_id ? { ...x, notes: v } : x)),
                        }));
                      }}
                      onBlur={(e) => update(it.check_id, it.status, e.target.value)}
                      fullWidth
                    />
                  </Stack>
                </Box>
              ))}
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <SectionCard
            title="Snapshots"
            description="היסטוריית ציונים"
            icon={<HistoryRoundedIcon />}
            iconTone="violet"
            sx={{ position: { lg: "sticky" }, top: { lg: 88 } }}
            action={
              <Button variant="outlined" size="small" onClick={createSnapshot} disabled={snapshotBusy}>
                {snapshotBusy ? "שומר..." : "שמור Snapshot"}
              </Button>
            }
          >
            {history?.runs?.length ? (
              <Stack spacing={1.25}>
                {history.runs.slice(0, 6).map((r) => (
                  <Box key={r.id} sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, p: 1.5, display: "grid", gap: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", flexGrow: 1 }}>{new Date(r.created_at).toLocaleString("he-IL")}</Typography>
                      <Pill tone={r.score_percent >= 80 ? "success" : r.score_percent >= 60 ? "warning" : "error"}>
                        {r.score_percent}% ({r.score_grade})
                      </Pill>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, Number(r.score_percent || 0)))}
                      aria-label={`ציון ${r.score_percent}%`}
                    />
                    <Button
                      component={RouterLink}
                      to={`/sites/${id}/compliance-report?runId=${encodeURIComponent(r.id)}`}
                      size="small"
                      variant="text"
                      sx={{ justifySelf: "start", minHeight: 28, px: 1 }}
                    >
                      דוח Snapshot
                    </Button>
                  </Box>
                ))}
              </Stack>
            ) : (
              <EmptyState
                sx={{ py: 3 }}
                icon={<HistoryRoundedIcon />}
                title="אין Snapshots עדיין"
                description="מומלץ לשמור אחד אחרי שינויים משמעותיים או פעם בחודש."
              />
            )}

            {history?.runs?.length >= 2 ? (
              <Button
                component={RouterLink}
                to={`/sites/${id}/compliance-diff?from=${encodeURIComponent(history.runs[1].id)}&to=${encodeURIComponent(history.runs[0].id)}`}
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
              >
                מה השתנה מאז Snapshot קודם
              </Button>
            ) : null}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
